import axiosInstance from "../lib/axios";
import type { HomeSectionKey } from "../type/homePage";

/**
 * API Home Page (backend: ARUNIKA-backend/src/homePage)
 *
 * Semua response memakai format standar backend:
 *   { result: boolean, resultCd: number, message: string, data?: ... }
 * Error validasi dibalas dengan `message` yang jelas -> ditampilkan apa
 * adanya di popup (lihat halaman edit).
 *
 * GET  /HomePage                 (publik)
 *   -> data: { header: {...}, hero: {...}, ...11 section }
 *   Dipakai halaman list untuk jumlah item & waktu update.
 *
 * GET  /HomePage/:section        (publik)
 *   -> data: { <field>: string | null, items?: [{ id, ...field }], updated_at }
 *   Field gambar berisi URL absolut (APP_URL + /uploads/...) atau null.
 *   Section yang belum pernah disimpan tetap 200 dengan field null & items [].
 *   Section key tidak dikenal -> 500 "Home section not found."
 *
 * PUT  /HomePage/:section        (multipart/form-data, wajib login Admin/Staff)
 *   Field teks    : dikirim langsung, mis. title, button_label
 *   Field EN      : field yang translatable (lihat `translatable: true` di
 *                   src/helpers/homePageSections.ts) juga dikirim versi
 *                   Inggrisnya dengan key `<field>_en`, mis. title_en,
 *                   button_label_en. Field yang tidak translatable (link,
 *                   phone, email, dst) TIDAK punya pasangan `_en`.
 *                   Versi EN opsional (boleh kosong, admin bisa isi
 *                   belakangan) -- validasi required hanya berlaku ke versi
 *                   Indonesia.
 *   Field gambar  : File baru dikirim dengan key field-nya, mis. image.
 *                   Gambar dihapus admin -> `remove_<key>=true`.
 *                   Tidak diubah -> tidak dikirim (backend pakai yang lama).
 *                   Gambar SELALU 1 (tidak ada versi EN terpisah).
 *   Item berulang : `items` = JSON string array
 *                     [{ id | null, sort_order, <field teks>, <field teks>_en (kalau translatable), <field gambar: URL lama | null> }]
 *                   Urutan array = urutan tampil. Item yang tidak ada di
 *                   array = dihapus. File gambar baru dikirim sebagai
 *                   `items[<index>][<key>]`.
 *   Status error  : 400 upload ditolak (bukan gambar / > 5 MB),
 *                   401 belum login / token kedaluwarsa (interceptor axios
 *                   otomatis mengarahkan ke /login),
 *                   403 role tidak diizinkan (mis. Sales),
 *                   500 validasi gagal (lihat `message`).
 *
 * Section key: header, hero, categories, about, materials, industries,
 * recent_project, clients, client_story, achievements, footer.
 * Daftar field tiap section harus selaras dengan
 * src/helpers/homePageSections.ts (frontend) dan
 * src/homePage/homePageSections.ts (backend), termasuk field mana yang
 * `translatable` (backend butuh flag yang sama untuk tahu field mana yang
 * punya pasangan `_en`).
 */

export const getHomePage = () => {
  return axiosInstance.get("/HomePage");
};

export const getHomePageSection = (section: HomeSectionKey) => {
  return axiosInstance.get(`/HomePage/${section}`);
};

export const updateHomePageSection = (
  section: HomeSectionKey,
  formData: FormData
) => {
  return axiosInstance.put(`/HomePage/${section}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    // Upload beberapa gambar sekaligus bisa lebih lama dari default 10 detik.
    timeout: 60000,
  });
};